import { useRef, useEffect } from 'react';
import { API_URL } from './constants';
import { authHeaders } from './helpers';
import type { Row } from './types';

interface HoursEntry {
  employee_id: string | number;
  hours_regular: number;
  hours_overtime: number;
  hours_stat_holiday: number;
  hours_vacation: number;
  hours_sick: number;
  stat_pay_amount: number;
  memo: string | null;
}

function rowsToEntries(rows: Row[]): HoursEntry[] {
  return rows.map((r) => ({
    employee_id: r.id,
    hours_regular: parseFloat(r.regular) || 0,
    hours_overtime: parseFloat(r.overtime) || 0,
    hours_stat_holiday: parseFloat(r.statHoliday) || 0,
    hours_vacation: parseFloat(r.vacation) || 0,
    hours_sick: parseFloat(r.sick) || 0,
    stat_pay_amount: parseFloat(String(r.statAvgDaily)) || 0,
    memo: r.memo || null,
  }));
}

export function saveHoursDraft(runId: string | undefined, rowsToSave: Row[]): void {
  if (!runId) return;
  const entries = rowsToEntries(rowsToSave || []);
  fetch(API_URL + '/api/v1/payroll/runs/' + runId + '/hours', {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ entries }),
  }).catch((e) => { console.error('Autosave failed', e); });
}

export interface AutosaveHandle {
  schedule: (runId: string | undefined, currentRows: Row[]) => void;
  flush: (runId: string | undefined, currentRows: Row[]) => void;
  cancel: () => void;
}

export function useAutosave(payRunId: string | undefined): AutosaveHandle {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRowsRef = useRef<Row[]>([]);

  const cancel = (): void => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  };

  const schedule = (runId: string | undefined, currentRows: Row[]): void => {
    latestRowsRef.current = currentRows;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveHoursDraft(runId, latestRowsRef.current);
    }, 1000);
  };

  const flush = (runId: string | undefined, currentRows: Row[]): void => {
    cancel();
    saveHoursDraft(runId, currentRows);
  };

  // On unmount, flush any pending save
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveHoursDraft(payRunId, latestRowsRef.current);
      }
    };
  }, [payRunId]);

  return { schedule, flush, cancel };
}